
import React from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import AIAssistant from '../components/AIAssistant';
import Services from '../components/Services';
import Process from '../components/Process';
import Testimonials from '../components/Testimonials';
import { 
  ShieldCheck, Globe, FileCheck, ArrowRight, 
  ChevronRight, Map, Building2, Landmark, 
  Cpu, Lock, CheckCircle2, Users, Briefcase, BookOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SERVICES } from '../constants';

const DiscoverySection = () => {
  return (
    <section className="py-24 bg-white relative border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-indigo-600 font-bold uppercase tracking-widest text-xs mb-6">
              <Globe size={16} /> Global Discovery
            </div>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-slate-900 mb-6 leading-tight">
              Connecting Clients to <br />
              <span className="text-slate-500">Trusted Experts.</span>
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              We route global mandates from India, UAE, and beyond directly to our network of qualified Chartered Accountants. Whether you need an Ind-AS specialist or a Dubai Tax Agent, find them here.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 border border-indigo-100 text-indigo-600">
                  <Briefcase size={20} />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold mb-1">Inbound Client Leads</h4>
                  <p className="text-sm text-slate-500">Direct inquiries for Statutory Audit, GST, and Virtual CFO roles routed to member CAs.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 border border-indigo-100 text-indigo-600">
                  <Users size={20} />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold mb-1">Co-Delivery & Referrals</h4>
                  <p className="text-sm text-slate-500">Collaborate with peers on complex cross-border assignments (e.g., India-UAE consolidation).</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="relative h-[400px] bg-slate-50 rounded-[3rem] border border-slate-200 overflow-hidden p-8 flex items-center justify-center shadow-xl">
            {/* Abstract Map Visual */}
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:24px_24px]" />
            <div className="relative z-10 w-full max-w-sm text-center">
               <div className="flex justify-between items-center mb-8">
                  <div className="text-center">
                    <div className="text-4xl mb-2">🇮🇳</div>
                    <div className="text-xs font-black text-slate-500 uppercase tracking-widest">Mumbai Hub</div>
                  </div>
                  <div className="h-px bg-indigo-200 flex-grow mx-4 relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 bg-white border border-indigo-100 rounded-full text-[9px] text-indigo-600 font-bold uppercase shadow-sm">
                      Work Flow
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-2">🇦🇪</div>
                    <div className="text-xs font-black text-slate-500 uppercase tracking-widest">Dubai Client</div>
                  </div>
               </div>
               <div className="p-6 bg-white/80 backdrop-blur-md rounded-2xl border border-slate-200 text-left shadow-lg">
                  <div className="flex items-center gap-2 mb-3">
                     <Users size={16} className="text-indigo-500" />
                     <span className="text-slate-900 font-bold text-sm">Lead Matched: CA Manthan</span>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                     <CheckCircle2 size={16} className="text-emerald-500" />
                     <span className="text-slate-900 font-bold text-sm">Engagement Letter Signed</span>
                  </div>
                  <div className="flex items-center gap-2">
                     <Briefcase size={16} className="text-amber-500" />
                     <span className="text-slate-900 font-bold text-sm">Work In Progress</span>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ExpertiseGrid = () => {
  return (
    <section id="services" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-6 shadow-sm">
            Professional Competencies
          </div>
          <h2 className="text-3xl md:text-5xl font-display font-bold text-slate-900 mb-6">Expertise & Practice Areas</h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            Showcase your specialized skills to the network or find the right expert for your business needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {SERVICES.map((service, i) => (
            <Link 
              key={service.id} 
              to={`/service/${service.id}`}
              className="group relative p-8 rounded-[2.5rem] bg-white border border-slate-200 hover:border-indigo-200 hover:shadow-2xl transition-all flex flex-col"
            >
               <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-indigo-600 mb-8 group-hover:scale-110 transition-transform shadow-sm">
                  {i === 0 && <ShieldCheck size={28} />}
                  {i === 1 && <Globe size={28} />}
                  {i === 2 && <FileCheck size={28} />}
               </div>

               <h3 className="text-xl font-display font-bold text-slate-900 mb-4 group-hover:text-indigo-600 transition-colors">{service.title}</h3>
               <p className="text-slate-500 text-sm leading-relaxed mb-8 flex-grow">
                 {service.shortDesc}
               </p>

               <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 group-hover:text-indigo-600 transition-colors">View Practice Area</span>
                  <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                    <ChevronRight size={16} />
                  </div>
               </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const IdentityCard = () => {
   return (
      <section className="py-24 bg-white border-y border-slate-200 relative">
         <div className="max-w-5xl mx-auto px-6">
            <div className="relative bg-slate-50 border border-slate-200 rounded-[3rem] overflow-hidden shadow-2xl">
               <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-indigo-100/50 to-transparent pointer-events-none" />
               
               <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-10 p-10 md:p-14 items-center">
                  <div className="md:col-span-4">
                     <div className="aspect-[3/4] bg-white rounded-3xl overflow-hidden border border-slate-200 relative shadow-xl">
                        <img src="/founder.jpeg" className="w-full h-full object-cover grayscale opacity-100" alt="CA Manthan Paliwal" />
                        <div className="absolute bottom-4 left-4">
                           <div className="px-3 py-1.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-lg">Founder</div>
                        </div>
                     </div>
                  </div>
                  <div className="md:col-span-8 space-y-8">
                     <div>
                        <div className="flex items-center gap-4 mb-4">
                           <Users size={24} className="text-indigo-600" />
                           <div className="h-px bg-slate-200 flex-grow" />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 mb-2">CA MANTHAN PALIWAL</h2>
                        <p className="text-indigo-600 font-mono text-xs uppercase tracking-[0.2em] font-bold">FCA • Community Architect</p>
                     </div>
                     <p className="text-slate-600 text-lg leading-relaxed max-w-xl">
                        "I built this platform to connect the fragmented world of Chartered Accountants. We are not just service providers; we are the <strong className="text-slate-900">architects of economic trust</strong>. Join us to share work, knowledge, and growth."
                     </p>
                     <div className="flex flex-wrap gap-4">
                        <Link to="/#contact" className="px-8 py-3 bg-indigo-600 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-600/20">
                           Partner with Me
                        </Link>
                        <Link to="/profile" className="px-8 py-3 bg-white border border-slate-200 text-slate-700 font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-slate-50 transition-colors">
                           View Founder Profile
                        </Link>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>
   )
}

const LiveTicker = () => {
   return (
      <div className="border-y border-slate-200 bg-white/80 backdrop-blur-sm overflow-hidden py-3">
         <div className="flex animate-marquee whitespace-nowrap gap-16 items-center">
            {[1, 2, 3, 4].map((i) => (
               <React.Fragment key={i}>
                  <div className="flex items-center gap-3 text-slate-500 font-mono text-xs font-bold uppercase tracking-widest">
                     <BookOpen size={12} className="text-amber-500" />
                     <span>New Insight: UAE Corporate Tax</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500 font-mono text-xs font-bold uppercase tracking-widest">
                     <Users size={12} className="text-indigo-500" />
                     <span>New Member: CA Rahul S. (Delhi)</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500 font-mono text-xs font-bold uppercase tracking-widest">
                     <Briefcase size={12} className="text-emerald-500" />
                     <span>Lead: Internal Audit (Bangalore)</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500 font-mono text-xs font-bold uppercase tracking-widest">
                     <Globe size={12} className="text-indigo-500" />
                     <span>Community Reach: 10k+</span>
                  </div>
               </React.Fragment>
            ))}
         </div>
      </div>
   )
}

const Home: React.FC = () => {
  return (
    <div className="bg-slate-50 selection:bg-indigo-100 selection:text-indigo-900">
      <Hero />
      <LiveTicker />
      <DiscoverySection />
      <ExpertiseGrid />
      <Services />
      <Process />
      <Testimonials />
      <IdentityCard />
      
      <section className="py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
           <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-indigo-600 text-white mb-8 shadow-xl shadow-indigo-600/30 ring-4 ring-indigo-50">
              <Users size={32} />
           </div>
           <h2 className="text-4xl md:text-6xl font-display font-bold text-slate-900 mb-8 tracking-tight">
              Expand Your <br /> Professional Influence.
           </h2>
           <Link to="/#contact" className="inline-flex items-center gap-4 px-10 py-5 bg-slate-900 text-white font-black uppercase tracking-widest text-sm rounded-2xl hover:bg-slate-800 transition-colors shadow-2xl">
              Join the Network <ArrowRight size={18} />
           </Link>
        </div>
      </section>

      <AIAssistant />
    </div>
  );
};

export default Home;
